import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, Grid, Divider, CircularProgress, Alert,
  Button, Chip, Table, TableHead, TableRow, TableCell, TableBody,
  LinearProgress, Tooltip, IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_URL  = `${BACKEND_URL}/api/setting/mercari-routing-settings/urgency-status`;
const SETTINGS_URL = `${BACKEND_URL}/api/setting/mercari-routing-settings`;

/** Defaults mirror backend DEFAULT_SETTINGS when API not loaded yet */
const FALLBACK_EVE_START = 7;
const FALLBACK_EVE_END = 22;

// ── Helpers (mirror orderRoutingService.getMerpayUrgency curve for the demo table) ──

function curvedTimeFactor(hour, eveStart, eveEnd, exp) {
  const span = eveEnd - eveStart;
  if (span <= 0) return 0;
  let linearElapsed = 0;
  if (hour >= eveStart && hour < eveEnd) {
    linearElapsed = (hour - eveStart) / span;
  } else if (hour >= eveEnd) {
    linearElapsed = 1;
  }
  return Math.pow(linearElapsed, exp);
}

/** depletionPct = % of aggregate cap used (same convention as the 5 demo rows) */
function calcUrgency(hour, depletionPct, eveStart, eveEnd, exp, wMerpay, wTime) {
  const curved = curvedTimeFactor(hour, eveStart, eveEnd, exp);
  return (depletionPct / 100) * wMerpay + curved * wTime;
}

function effectiveCap(urgency, tiers, baseMax) {
  const sorted = [...tiers].sort((a, b) => a.maxUrgency - b.maxUrgency);
  let pct = sorted[sorted.length - 1].merpayMaxPct;
  for (const tier of sorted) {
    if (urgency < tier.maxUrgency) { pct = tier.merpayMaxPct; break; }
  }
  return Math.round(baseMax * pct / 100);
}

function urgencyColor(pct) {
  if (pct >= 50) return 'success';
  if (pct >= 25) return 'warning';
  return 'error';
}

function fmt(n) { return Math.round(n).toLocaleString(); }

/** Same tier pick as backend getMerpayUrgency — maps urgency score → tier merpayMaxPct */
function merpayMaxPctForUrgency(urgency, urgencyTiers) {
  const sorted = [...(urgencyTiers || [])].sort((a, b) => a.maxUrgency - b.maxUrgency);
  if (!sorted.length) return 100;
  let pct = sorted[sorted.length - 1].merpayMaxPct;
  for (const tier of sorted) {
    if (urgency < tier.maxUrgency) {
      pct = tier.merpayMaxPct;
      break;
    }
  }
  return pct;
}

/**
 * success / warning / error tone from tier percentage — no hardcoded ¥ amounts.
 * Buckets the distinct merpayMaxPct values from settings into thirds (best → worst).
 */
function toneForMerpayTierPct(merpayMaxPct, urgencyTiers) {
  const pct = merpayMaxPct ?? 100;
  const unique = [...new Set((urgencyTiers || []).map((t) => t.merpayMaxPct))].sort((a, b) => b - a);
  if (unique.length === 0) {
    if (pct >= 86) return 'success.main';
    if (pct >= 50) return 'warning.main';
    return 'error.main';
  }
  const rank = unique.indexOf(pct);
  if (rank === -1) return 'text.primary';
  const n = unique.length;
  const third = Math.max(1, Math.ceil(n / 3));
  if (rank < third) return 'success.main';
  if (rank < third * 2) return 'warning.main';
  return 'error.main';
}

function demoRowBgFromTierPct(tierPct, urgencyTiers) {
  const tone = toneForMerpayTierPct(tierPct, urgencyTiers);
  if (tone === 'error.main') return 'rgba(211,47,47,0.07)';
  if (tone === 'warning.main') return 'rgba(237,108,2,0.07)';
  return undefined;
}

/** Label for a tier row: how "strict" vs other configured tiers (by merpayMaxPct rank). */
function tierSeverityLabel(tier, allTiersSorted) {
  const unique = [...new Set(allTiersSorted.map((t) => t.merpayMaxPct))].sort((a, b) => b - a);
  const rank = unique.indexOf(tier.merpayMaxPct);
  if (rank === -1 || unique.length <= 1) {
    return tier.merpayMaxPct === 100
      ? 'ปกติ — Mercari รับทุก order'
      : 'order ที่เกิน ceiling → Eve';
  }
  const n = unique.length;
  const third = Math.max(1, Math.ceil(n / 3));
  if (rank < third) return 'ปกติ — Mercari รับตาม ceiling';
  if (rank < third * 2) return 'ปานกลาง — order เกิน ceiling → Eve';
  return 'สูง — ส่วนใหญ่ → Eve';
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function MerpayUrgencyExplorerSetting() {
  const [status,   setStatus]   = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [sRes, cRes] = await Promise.all([
        axios.get(STATUS_URL),
        axios.get(SETTINGS_URL),
      ]);
      setStatus(sRes.data);
      setSettings(cRes.data);
      setError(null);
    } catch (e) {
      setError('ไม่สามารถโหลดข้อมูลได้');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 60_000); // auto-refresh every minute
    return () => clearInterval(id);
  }, [fetchAll]);

  const scenarios = useMemo(() => [
    { label: 'เช้า — เริ่มต้น', hour: 9, pct: 0 },
    { label: 'เที่ยง — ใช้งานน้อย', hour: 12, pct: 21 },
    { label: 'บ่าย — ยุ่ง', hour: 15, pct: 52 },
    { label: 'เย็น — ใช้มาก', hour: 18, pct: 75 },
    { label: 'ใกล้ Eve นอน', hour: 21, pct: 87 },
  ], []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  }

  // All urgency demo math uses the same Mercari routing settings as the backend
  const eveStart = settings?.urgencyWindowStartThai ?? FALLBACK_EVE_START;
  const eveEnd = settings?.urgencyWindowEndThai ?? FALLBACK_EVE_END;
  const wMerpay = settings?.urgencyWeightMerpayDepletion ?? 0.6;
  const wTime = settings?.urgencyWeightTime ?? 0.4;
  const exponent = settings?.urgencyTimeExponent ?? 3.5;
  const baseMax = settings?.merpayBaseMaxJpy ?? 70000;
  const tiers = settings?.urgencyTiers ?? [];
  const totalCap = status?.totalCap || 575000;

  const remainingPct = status?.merpayRemainingPct ?? 100;
  const urgencyScore = status?.urgency ?? 0;
  const capPerOrder  = status?.effectiveMerpayMax ?? baseMax;
  const timeFactor   = status?.timeFactor ?? 0;
  const carryOver    = status?.totalCarryOver ?? 0;
  const tomorrowOn   = status?.tomorrowFundEnabledAny ?? false;
  const tomorrowExtra = status?.tomorrowFundExtraCap ?? 0;

  /** Tier % of merpayBaseMaxJpy — drives ceiling ¥; same source as backend */
  const activeTierPct =
    status?.merpayMaxPct ?? merpayMaxPctForUrgency(urgencyScore, tiers);
  const tierTone = toneForMerpayTierPct(activeTierPct, tiers);

  const tiersSorted = [...tiers].sort((a, b) => a.maxUrgency - b.maxUrgency);

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <Typography variant="h5" fontWeight={600}>Merpay Urgency Monitor</Typography>
        <Chip
          label={tomorrowOn ? `Tomorrow fund +¥${fmt(tomorrowExtra)}` : 'Tomorrow fund: off'}
          color={tomorrowOn ? 'success' : 'default'}
          size="small"
        />
        <Box flex={1} />
        <Tooltip title="Refresh">
          <IconButton onClick={fetchAll} size="small"><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        อัปเดตอัตโนมัติทุก 1 นาที · สูตร demo ตรงกับ backend: depletion × {wMerpay.toFixed(2)} + time^{exponent.toFixed(1)} × {wTime.toFixed(2)} · Eve {eveStart}:00–{eveEnd}:00 ไทย
      </Typography>

      {/* Live stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Urgency Score', value: urgencyScore.toFixed(2),
            color: tierTone },
          { label: 'Merpay คงเหลือ', value: `${remainingPct}%`,
            color: urgencyColor(remainingPct) + '.main' },
          {
            label: 'Ceiling ต่อ order',
            value: `¥${fmt(capPerOrder)}`,
            color: tierTone,
            sub: `${activeTierPct}% ของ base (¥${fmt(baseMax)})`,
          },
          { label: 'คงเหลือรวม', value: `¥${fmt(status?.totalMerpayRemainingJpy ?? 0)}` },
          { label: 'ใช้ไปวันนี้', value: `¥${fmt(status?.totalUsed ?? 0)}` },
          { label: 'Carry-over', value: `¥${fmt(carryOver)}`,
            sub: carryOver > 0 ? 'บัฟเฟอร์จากเมื่อวาน' : undefined },
        ].map((item) => (
          <Grid item xs={6} sm={4} md={2} key={item.label}>
            <Paper sx={{ p: 1.5, textAlign: 'center', height: '100%' }}>
              <Typography variant="caption" color="text.secondary">{item.label}</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ color: item.color || 'text.primary' }}>
                {item.value}
              </Typography>
              {item.sub && (
                <Typography variant="caption" color="text.secondary">{item.sub}</Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Merpay remaining bar */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Merpay remaining — effective cap = ¥{fmt(status?.effectiveTotalCap ?? totalCap)}
          {tomorrowOn && ` (base ¥${fmt(status?.totalCap ?? totalCap)} + tomorrow ¥${fmt(tomorrowExtra)})`}
          {carryOver > 0 && `  ·  carry-over ¥${fmt(carryOver)} applied`}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={remainingPct}
          color={urgencyColor(remainingPct)}
          sx={{ height: 10, borderRadius: 1 }}
        />
      </Box>

      {/* Time factor */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Time factor (curved) — {timeFactor}% of max · Eve window {eveStart}:00–{eveEnd}:00
        </Typography>
        <LinearProgress
          variant="determinate"
          value={timeFactor}
          color="info"
          sx={{ height: 10, borderRadius: 1 }}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Per-account breakdown */}
      <Typography variant="h6" sx={{ mb: 1 }}>รายละเอียดต่อ ID</Typography>
      <Table size="small" sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell>Account</TableCell>
            <TableCell align="right">Cap / วัน</TableCell>
            <TableCell align="center">TF</TableCell>
            <TableCell align="right">Cap ใน urgency</TableCell>
            <TableCell align="right">ใช้วันนี้</TableCell>
            <TableCell align="right">Carry-over</TableCell>
            <TableCell align="right">คงเหลือ</TableCell>
            <TableCell align="right">%</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(status?.accountBreakdown ?? []).map((a) => {
            const effCap = a.effectiveCapForUrgency ?? a.cap;
            const remaining = Math.max(0, a.cap - a.usedToday);
            const pct = a.cap > 0 ? Math.round(remaining / a.cap * 100) : 100;
            return (
              <TableRow key={a.id}>
                <TableCell>{a.label}</TableCell>
                <TableCell align="right">¥{fmt(a.cap)}</TableCell>
                <TableCell align="center">
                  {a.tomorrowFundEnabled ? <Chip label="ON" size="small" color="success" /> : '—'}
                </TableCell>
                <TableCell align="right">¥{fmt(effCap)}</TableCell>
                <TableCell align="right">¥{fmt(a.usedToday)}</TableCell>
                <TableCell align="right" sx={{ color: a.carryOver > 0 ? 'success.main' : 'text.secondary' }}>
                  {a.carryOver > 0 ? `+¥${fmt(a.carryOver)}` : '—'}
                </TableCell>
                <TableCell align="right">¥{fmt(remaining)}</TableCell>
                <TableCell align="right">
                  <Chip label={`${pct}%`} size="small" color={urgencyColor(pct)} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Divider sx={{ my: 3 }} />

      {/* Urgency tier reference */}
      <Typography variant="h6" sx={{ mb: 1 }}>Urgency Tiers → Ceiling</Typography>
      <Table size="small" sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell>ช่วง urgency</TableCell>
            <TableCell align="right">%ของ base (¥{fmt(baseMax)})</TableCell>
            <TableCell align="right">Ceiling ต่อ order</TableCell>
            <TableCell>ผล</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(tiersSorted.length > 0 ? tiersSorted : []).map((tier, i, arr) => {
            const from = i === 0 ? 0 : arr[i - 1].maxUrgency;
            const cap_ = Math.round(baseMax * tier.merpayMaxPct / 100);
            const active = urgencyScore >= from && urgencyScore < tier.maxUrgency;
            return (
              <TableRow key={tier.maxUrgency} sx={{ bgcolor: active ? 'action.selected' : undefined }}>
                <TableCell>{from.toFixed(2)} – {tier.maxUrgency.toFixed(2)}</TableCell>
                <TableCell align="right">{tier.merpayMaxPct}%</TableCell>
                <TableCell align="right" fontWeight={active ? 700 : 400}>
                  ¥{fmt(cap_)}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {tierSeverityLabel(tier, arr)}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Divider sx={{ my: 3 }} />

      {/* 5-scenario table */}
      <Typography variant="h6" sx={{ mb: 0.5 }}>5 ตัวอย่างตามเวลา</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        คำนวณจากค่าใน Mercari Routing Settings (เช่น Eve window, weights, exponent, tiers) · คอลัมน์ Linear ใช้ exponent = 1 เพื่อเปรียบเทียบรูปทรงเส้นเท่านั้น
      </Typography>
      {tiers.length > 0 && (
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>สถานการณ์</TableCell>
              <TableCell align="right">Merpay ใช้ไป</TableCell>
              <TableCell align="right">Time factor</TableCell>
              <TableCell align="right">Urgency</TableCell>
              <TableCell align="right">Ceiling (curved)</TableCell>
              <TableCell align="right">Linear (exp=1)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {scenarios.map((s_) => {
              const curved   = curvedTimeFactor(s_.hour, eveStart, eveEnd, exponent);
              const urg      = calcUrgency(s_.hour, s_.pct, eveStart, eveEnd, exponent, wMerpay, wTime);
              const cap_     = effectiveCap(urg, tiers, baseMax);
              const linUrg   = (s_.pct / 100) * wMerpay + curvedTimeFactor(s_.hour, eveStart, eveEnd, 1) * wTime;
              const linCap   = effectiveCap(linUrg, tiers, baseMax);
              const diffText = linCap !== cap_ ? `¥${fmt(linCap)}` : 'เท่ากัน';
              const demoTierPct = merpayMaxPctForUrgency(urg, tiers);
              const bg = demoRowBgFromTierPct(demoTierPct, tiers);
              return (
                <TableRow key={s_.hour} sx={{ bgcolor: bg }}>
                  <TableCell>{String(s_.hour).padStart(2,'0')}:00 {s_.label}</TableCell>
                  <TableCell align="right">
                    ¥{fmt(totalCap * s_.pct / 100)} ({s_.pct}%)
                  </TableCell>
                  <TableCell align="right">{curved.toFixed(5)}</TableCell>
                  <TableCell align="right">{urg.toFixed(3)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>¥{fmt(cap_)}</TableCell>
                  <TableCell align="right" sx={{ color: linCap !== cap_ ? 'warning.main' : 'text.secondary' }}>
                    {diffText}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Typography variant="caption" color="text.secondary">
        Ceiling คือวงเงิน Merpay สูงสุดต่อ order — order ที่เกิน ceiling จะถูกส่ง Eve แทน
      </Typography>
    </Box>
  );
}
